import { Box3, Box3Helper, Color, Group, Material, Mesh, Plane, Scene, Vector3, type Object3D } from 'three';
import { EventEmitter } from '../core/EventEmitter';

export type BoxFace = 'xmin' | 'xmax' | 'ymin' | 'ymax' | 'zmin' | 'zmax';

export class BoxCutController {
  readonly changed = new EventEmitter<boolean>();
  private readonly box = new Box3();
  private readonly helper = new Box3Helper(this.box, new Color(0x27b6ff));
  private active = false;

  constructor(private readonly modelsRoot: Group, private readonly scene: Scene) {
    this.helper.name = 'box-cut-helper';
  }

  start(target: Object3D): void {
    this.box.setFromObject(target);
    if (this.box.isEmpty()) this.box.setFromObject(this.modelsRoot);
    if (this.box.isEmpty()) return;
    const padding = this.box.getSize(new Vector3()).multiplyScalar(0.08);
    this.box.min.sub(padding);
    this.box.max.add(padding);
    this.active = true;
    this.scene.add(this.helper);
    this.apply();
    this.changed.emit(true);
  }

  clear(): void {
    this.active = false;
    this.scene.remove(this.helper);
    this.modelsRoot.traverse((object) => {
      if (object instanceof Mesh) for (const material of this.materialsOf(object)) material.clippingPlanes = null;
    });
    this.changed.emit(false);
  }

  moveFace(face: BoxFace, delta: number): void {
    if (!this.active) return;
    const gap = Math.max(this.box.getSize(new Vector3()).length() * 0.002, 0.001);
    if (face === 'xmin') this.box.min.x = Math.min(this.box.min.x + delta, this.box.max.x - gap);
    if (face === 'xmax') this.box.max.x = Math.max(this.box.max.x + delta, this.box.min.x + gap);
    if (face === 'ymin') this.box.min.y = Math.min(this.box.min.y + delta, this.box.max.y - gap);
    if (face === 'ymax') this.box.max.y = Math.max(this.box.max.y + delta, this.box.min.y + gap);
    if (face === 'zmin') this.box.min.z = Math.min(this.box.min.z + delta, this.box.max.z - gap);
    if (face === 'zmax') this.box.max.z = Math.max(this.box.max.z + delta, this.box.min.z + gap);
    this.apply();
    this.changed.emit(true);
  }

  get handles(): Array<{ face: BoxFace; point: Vector3; direction: Vector3 }> {
    if (!this.active) return [];
    const center = this.box.getCenter(new Vector3());
    return [
      { face: 'xmin', point: new Vector3(this.box.min.x, center.y, center.z), direction: new Vector3(1, 0, 0) },
      { face: 'xmax', point: new Vector3(this.box.max.x, center.y, center.z), direction: new Vector3(1, 0, 0) },
      { face: 'ymin', point: new Vector3(center.x, this.box.min.y, center.z), direction: new Vector3(0, 1, 0) },
      { face: 'ymax', point: new Vector3(center.x, this.box.max.y, center.z), direction: new Vector3(0, 1, 0) },
      { face: 'zmin', point: new Vector3(center.x, center.y, this.box.min.z), direction: new Vector3(0, 0, 1) },
      { face: 'zmax', point: new Vector3(center.x, center.y, this.box.max.z), direction: new Vector3(0, 0, 1) },
    ];
  }

  synchronize(): void { if (this.active) this.apply(); }

  containsPoint(point: Vector3): boolean {
    return !this.active || this.box.containsPoint(point);
  }

  private apply(): void {
    const p = [
      new Plane(new Vector3(1, 0, 0), -this.box.min.x), new Plane(new Vector3(-1, 0, 0), this.box.max.x),
      new Plane(new Vector3(0, 1, 0), -this.box.min.y), new Plane(new Vector3(0, -1, 0), this.box.max.y),
      new Plane(new Vector3(0, 0, 1), -this.box.min.z), new Plane(new Vector3(0, 0, -1), this.box.max.z),
    ];
    this.modelsRoot.traverse((object) => {
      if (object instanceof Mesh) for (const material of this.materialsOf(object)) material.clippingPlanes = p;
    });
    this.helper.updateMatrixWorld(true);
  }

  private materialsOf(mesh: Mesh): Material[] { return Array.isArray(mesh.material) ? mesh.material : [mesh.material]; }
}

